import React, { useState, useEffect } from 'react';
import { chatService } from '../../services/chatService';

interface KnowledgeTextInputProps {
  // No props needed - component manages its own state
}

const KnowledgeTextInput: React.FC<KnowledgeTextInputProps> = () => {
  const [knowledgeText, setKnowledgeText] = useState<string>('');
  const [addingKnowledge, setAddingKnowledge] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchChatMeta = async () => {
    setLoadingMeta(true);
    try {
      const metaData = await chatService.getChatMeta();
      setTags(metaData.tags || []);
      setTypes(metaData.type_list || []);
    } catch (error) {
      console.error('Failed to fetch chat meta:', error);
    } finally {
      setLoadingMeta(false);
    }
  };

  const handleAddKnowledge = async () => {
    if (!knowledgeText.trim()) return;

    setAddingKnowledge(true);
    setNotification(null);
    try {
      const knowledgeData = {
        text: knowledgeText,
        tag: selectedTag,
        type: selectedType
      };
      await chatService.addKnowledge(knowledgeData);
      setKnowledgeText('');
      setSelectedTag('');
      setSelectedType('');
      setNotification({ type: 'success', message: 'Knowledge added successfully!' });
    } catch (error) {
      console.error('Failed to add knowledge:', error);
      setNotification({ type: 'error', message: 'Failed to add knowledge. Please try again.' });
    } finally {
      setAddingKnowledge(false);
    }
  };

  useEffect(() => {
    fetchChatMeta();
  }, []);
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Add Knowledge Here</h3>
      <textarea 
        className="input-field resize-none"
        placeholder="Enter knowledge content..."
        rows={6}
        value={knowledgeText}
        onChange={(e) => setKnowledgeText(e.target.value)}
      />
      
      
      {/* Select dropdowns - only show if lists have items */}
      {(tags.length > 0 || types.length > 0) && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {tags.length > 0 && (
            <div>
              <label htmlFor="tags-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Tags
              </label>
              <select
                id="tags-select"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                disabled={loadingMeta}
              >
                <option value="">Select a tag...</option>
                {tags.map((tag, index) => (
                  <option key={index} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {types.length > 0 && (
            <div>
              <label htmlFor="types-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Types
              </label>
              <select
                id="types-select"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                disabled={loadingMeta}
              >
                <option value="">Select a type...</option>
                {types.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      <button 
        className="btn-primary mt-3"
        onClick={handleAddKnowledge}
        disabled={addingKnowledge || !knowledgeText.trim() || 
          (tags.length > 0 && !selectedTag) || 
          (types.length > 0 && !selectedType)}
      >
        {addingKnowledge ? 'Adding...' : 'Add'}
      </button>
      
      {loadingMeta && (
        <div className="mt-2 text-sm text-gray-500">
          Loading options...
        </div>
      )}
      
      {notification && (
        <div className={`mt-4 p-4 rounded-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-300 text-green-800' 
            : 'bg-red-100 border border-red-300 text-red-800'
        }`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default KnowledgeTextInput;
